import React, { useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { Shield, Loader2, CheckCircle, AlertTriangle } from "lucide-react";
import { acceptAdminInvite } from "@/api/services/adminManagementService";
import { getErrorMessage } from "@/utils/errorUtils";

// Public, token-gated page: an invited admin sets their name + password (step 2),
// which creates the admin account. They then sign in via Auth0.
const AcceptAdminInvite: React.FC = () => {
  const [params] = useSearchParams();
  const token = useMemo(() => params.get("token") || "", [params]);

  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [err, setErr] = useState("");

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErr("");
    if (!token) { setErr("This invitation link is invalid or has expired."); return; }
    if (!firstName.trim() || !lastName.trim()) { setErr("Please enter your first and last name."); return; }
    if (password.length < 8) { setErr("Password must be at least 8 characters."); return; }
    if (password !== confirm) { setErr("Passwords do not match."); return; }

    setSubmitting(true);
    try {
      await acceptAdminInvite({ token, firstName: firstName.trim(), lastName: lastName.trim(), password });
      setDone(true);
    } catch (e: unknown) {
      setErr(getErrorMessage(e) || "Could not accept the invitation. The link may have expired.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <Helmet>
        <title>Accept admin invitation | KonectX</title>
      </Helmet>
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-black p-4">
        <div className="w-full max-w-md rounded-2xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-[#1c1c1c] p-6 sm:p-8">
          <div className="flex items-center gap-3 mb-2">
            <Shield className="w-6 h-6 text-blue-600" />
            <h1 className="text-xl font-bold text-gray-900 dark:text-white">Accept admin invitation</h1>
          </div>

          {done ? (
            <div className="mt-4 text-center">
              <CheckCircle className="w-12 h-12 text-green-500 mx-auto" />
              <h2 className="mt-3 font-semibold text-gray-900 dark:text-white">Your admin account is ready</h2>
              <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                Sign in with the identity provider to access the admin panel.
              </p>
              <button
                onClick={() => { window.location.href = `${import.meta.env.VITE_API_URL}/auth/auth0/login?role=admin`; }}
                className="mt-5 w-full rounded-lg bg-[#2E5A88] px-4 py-2.5 text-sm font-semibold text-white hover:bg-[#244a73]"
              >
                Sign in
              </button>
            </div>
          ) : !token ? (
            <div className="mt-4 flex items-start gap-3 rounded-lg bg-red-50 dark:bg-red-900/20 p-3">
              <AlertTriangle className="w-5 h-5 text-red-600 shrink-0" />
              <p className="text-sm text-red-700 dark:text-red-400">
                This invitation link is missing its token or has expired. Ask a super-admin to re-send your invite.
              </p>
            </div>
          ) : (
            <form onSubmit={submit} className="mt-4 space-y-3">
              <p className="text-sm text-gray-500 dark:text-gray-400">
                Set your name and a password to finish creating your admin account.
              </p>
              {err && (
                <div className="flex items-center gap-2 rounded-lg bg-red-50 dark:bg-red-900/20 px-3 py-2 text-sm text-red-700 dark:text-red-400">
                  <AlertTriangle className="w-4 h-4" /> {err}
                </div>
              )}
              <div className="grid grid-cols-2 gap-3">
                <input value={firstName} onChange={(e) => setFirstName(e.target.value)} placeholder="First name"
                  className="rounded-lg border border-gray-300 dark:border-gray-700 bg-transparent px-3 py-2.5 text-sm text-gray-900 dark:text-white outline-none focus:ring-2 focus:ring-blue-500" />
                <input value={lastName} onChange={(e) => setLastName(e.target.value)} placeholder="Last name"
                  className="rounded-lg border border-gray-300 dark:border-gray-700 bg-transparent px-3 py-2.5 text-sm text-gray-900 dark:text-white outline-none focus:ring-2 focus:ring-blue-500" />
              </div>
              <input type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Password"
                className="w-full rounded-lg border border-gray-300 dark:border-gray-700 bg-transparent px-3 py-2.5 text-sm text-gray-900 dark:text-white outline-none focus:ring-2 focus:ring-blue-500" />
              <input type="password" value={confirm} onChange={(e) => setConfirm(e.target.value)} placeholder="Confirm password"
                className="w-full rounded-lg border border-gray-300 dark:border-gray-700 bg-transparent px-3 py-2.5 text-sm text-gray-900 dark:text-white outline-none focus:ring-2 focus:ring-blue-500" />
              <p className="text-xs text-gray-400">Min 8 characters, with uppercase, lowercase, number and special character.</p>
              <button type="submit" disabled={submitting}
                className="w-full rounded-lg bg-[#2E5A88] px-4 py-2.5 text-sm font-semibold text-white hover:bg-[#244a73] disabled:opacity-50 inline-flex items-center justify-center gap-2">
                {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
                Create admin account
              </button>
            </form>
          )}
        </div>
      </div>
    </>
  );
};

export default AcceptAdminInvite;
